// src/pages/ProductDetails.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { getOrCreateChat } from "../utils/chatHelpers";
import { resolveUserDisplayName } from "../utils/userIdentity";
import { applyRouteSeo, SITE_ORIGIN } from "../seo";
import OfferModal from "../components/OfferModal";
import BuyNowGuidanceModal from "../components/BuyNowGuidanceModal";
import useTransactionalAuthGuard, { consumePendingTransactionalAction } from "../hooks/useTransactionalAuthGuard";
import { useToast } from "../contexts/ToastContext";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { formatCountdownDisplay } from "../utils/formatExpiration";
import ReportEntityDialog from "../components/reports/ReportEntityDialog";
import { submitEntityReport, REPORT_ENTITY_TYPES } from "../services/reportsService";
import { ratingsRepository } from "../repositories/ratingsRepository";
import { userRepository } from "../repositories/userRepository";
import { productRepository } from "../repositories/productRepository";
import { errorDev } from "../utils/logger.js";
import SimilarProducts from "../components/farm/SimilarProducts";
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  ShieldCheck,
  MoreVertical,
  Edit3,
  EyeOff,
  Eye,
  Trash2,
  MapPin,
  Package,
  User,
  MessageSquare,
  DollarSign,
  Calendar,
  Leaf,
  Flag,
} from "lucide-react";

/* ---------------- Helpers ---------------- */

function extractExpiry(p) {
  const raw =
    p?.expiry?.toDate?.() ||
    (p?.expiry ? new Date(p.expiry) : null) ||
    p?.expiresAt?.toDate?.() ||
    (p?.expiresAt ? new Date(p.expiresAt) : null) ||
    (p?.expiryDate ? new Date(p.expiryDate) : null) ||
    (p?.sellUntil ? new Date(p.sellUntil) : null);
  return raw && !isNaN(raw.getTime()) ? raw : null;
}
function formatPrice(price, currency) {
  if (price === 0 || price === "0") return "Free";
  if (price == null || price === "") return "Price on request";
  const n = Number(price);
  if (Number.isFinite(n)) {
    try {
      return currency
        ? new Intl.NumberFormat(undefined, {
            style: "currency",
            currency,
            currencyDisplay: "narrowSymbol",
            maximumFractionDigits: 0,
          }).format(n)
        : n.toLocaleString();
    } catch (err) {
      errorDev("Operation failed:", err);
      return `${n.toLocaleString()} ${currency || ""}`;
    }
  }
  return `${price} ${currency || ""}`;
}

function formatRwf(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n)} RWF`;
}

function getPricePerKg(product) {
  const n = Number(product?.pricePerKg ?? product?.price);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function getQuantityKg(product) {
  const q = Number(product?.quantity);
  return Number.isFinite(q) && q > 0 ? q : null;
}

function formatQuantityLabel(product, t) {
  const q = Number(product?.quantity);
  if (!Number.isFinite(q) || q <= 0) return null;
  const unit = product?.quantityUnit || product?.unit || "kg";
  const unitUpper = String(unit).toUpperCase();
  let unitLabel;
  switch (unitUpper) {
    case "BIRD":
      unitLabel = t("product:form.animal.units.bird", "Bird");
      break;
    case "EGG":
      unitLabel = t("product:form.animal.units.egg", "Egg");
      break;
    case "TRAY":
      unitLabel = t("product:form.animal.units.tray", "Tray");
      break;
    default:
      unitLabel = unitUpper;
  }
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(q)} ${unitLabel}`;
}

function formatAnimalTotal(product, t) {
  const unitPrice = Number(product?.pricePerKg ?? product?.price);
  const quantity = Number(product?.quantity);
  if (!Number.isFinite(unitPrice) || !Number.isFinite(quantity) || unitPrice < 0 || quantity <= 0) {
    return null;
  }
  return Math.round(unitPrice * quantity);
}
function primaryImage(product) {
  return product?.images?.[0] || product?.image || product?.thumbnail || "/placeholder-product.png";
}
function formatLocationShort(loc) {
  if (!loc || typeof loc !== "object") return null;
  const cell = loc.cell || loc.village || null;
  const sector = loc.sector || null;
  const district = loc.district || null;
  const parts = [cell, sector, district].filter(Boolean);
  return parts.length ? `[${parts.join(", ")}]` : null;
}
/** Remove possible path segments and coerce to string */
function sanitizeId(v) {
  if (!v && v !== 0) return null;
  const s = String(v);
  if (s.includes("/")) return s.split("/").pop();
  return s;
}

/* ---------------- Image Modal ---------------- */

function ImageModal({ images = [], start = 0, onClose }) {
  const [index, setIndex] = useState(start || 0);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [images.length, onClose]);

  if (!images || images.length === 0) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[1200] bg-black/85 flex flex-col items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-h-[88vh] max-w-[96vw]" onClick={(e) => e.stopPropagation()}>
        <button
          aria-label="Close image viewer"
          className="absolute right-2 top-2 z-20 bg-white/90 rounded-full p-2"
          onClick={onClose}
        >
          ✕
        </button>

        <img
          src={images[index]}
          alt={`Image ${index + 1}`}
          onError={(e) => (e.currentTarget.src = "/placeholder-product.png")}
          className="max-h-[80vh] max-w-[90vw] object-contain rounded"
        />

        {images.length > 1 && (
          <>
            <button
              aria-label="Previous image"
              onClick={() => setIndex((i) => (i - 1 + images.length) % images.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-2"
            >
              ‹
            </button>
            <button
              aria-label="Next image"
              onClick={() => setIndex((i) => (i + 1) % images.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-2"
            >
              ›
            </button>

            <div className="mt-3 flex gap-2 justify-center overflow-x-auto">
              {images.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`Thumb ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={`w-16 h-16 object-cover rounded border-4 cursor-pointer ${
                    i === index ? "border-green-500" : "border-transparent"
                  }`}
                  onError={(e) => (e.currentTarget.src = "/placeholder-product.png")}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Main Component ---------------- */

export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["farmMarketplace", "product", "common"]);
  const toast = useToast();
  const { user: currentUser } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [countdown, setCountdown] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  const [privateBlocked, setPrivateBlocked] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [submittingReport, setSubmittingReport] = useState(false);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [sellerRatingStats, setSellerRatingStats] = useState({ avg: null, count: 0 });
  const [resumeOfferAmount, setResumeOfferAmount] = useState("");
  const { requireAuthForTransactionalAction } = useTransactionalAuthGuard(currentUser);

  // Live product snapshot; respects hidden/unlisted for non-owner
  useEffect(() => {
    setLoading(true);
    setPrivateBlocked(false);

    // The repository owns the document ref and the listener lifecycle so this
    // page does not build a Firestore path itself. Visibility filtering is a
    // UI concern and stays here.
    const unsub = productRepository.subscribeById(
      id,
      (row) => {
        if (!row) {
          setProduct(null);
          setLoading(false);
          return;
        }
        const viewerIsOwner =
          currentUser && (row.userId === currentUser.uid || row.poster?.userId === currentUser.uid);
        if ((row.hidden || row.isHidden || row.isPrivate) && !viewerIsOwner) {
          setProduct(null);
          setPrivateBlocked(true);
        } else {
          setProduct(row);
          setPrivateBlocked(false);
        }
        setLoading(false);
      },
      (err) => {
        // Product snapshot error silently handled
        productRepository
          .getById(id)
          .then((row) => {
            if (!row) {
              setProduct(null);
            } else {
              const viewerIsOwner =
                currentUser &&
                (row.userId === currentUser.uid || row.poster?.userId === currentUser.uid);
              if ((row.hidden || row.isHidden || row.isPrivate) && !viewerIsOwner) {
                setProduct(null);
                setPrivateBlocked(true);
              } else {
                setProduct(row);
              }
            }
          })
          .catch((e) => {
            /* silently handled */
          })
          .finally(() => setLoading(false));
      }
    );
    return () => unsub();
  }, [id, currentUser]);

  // countdown logic
  const expiry = useMemo(() => extractExpiry(product || {}), [product]);
  useEffect(() => {
    setCountdown(formatCountdownDisplay(expiry));
    if (!expiry) return;
    const tid = setInterval(() => setCountdown(formatCountdownDisplay(expiry)), 60_000); // update every minute
    return () => clearInterval(tid);
  }, [expiry]);

  const isOwner = !!(
    currentUser &&
    (product?.userId === currentUser.uid || product?.poster?.userId === currentUser.uid)
  );

  // kebab outside click / esc close
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    window.addEventListener("mousedown", onDocClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // UI state derived
  const images = product?.images && product.images.length > 0
    ? product.images
    : [product?.image || product?.thumbnail || "/placeholder-product.png"];

  const isExpired = countdown === "Expired";

  // actions
  const goBack = () => navigate(-1);

  const resolveSellerId = (p) =>
    p?.userId || p?.poster?.userId || p?.sellerId || p?.poster?.uid || null;

  const firstPositiveNumber = (...values) => {
    for (const v of values) {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  };

  useEffect(() => {
    let cancelled = false;
    const loadSellerProfile = async () => {
      const sellerId = sanitizeId(resolveSellerId(product));
      if (!sellerId) {
        if (!cancelled) setSellerProfile(null);
        return;
      }
      try {
        // Profile reads go through userRepository so this page does not build
        // the users/{uid} path itself.
        const seller = await userRepository.getById(sellerId);
        if (!cancelled) {
          setSellerProfile(seller);
        }
      } catch (err) {
        errorDev("Operation failed:", err);
        if (!cancelled) setSellerProfile(null);
      }
    };
    loadSellerProfile();
    return () => {
      cancelled = true;
    };
  }, [product?.id, product?.userId, product?.poster?.userId, product?.sellerId, product?.poster?.uid]);

useEffect(() => {
    let cancelled = false;
    const loadSellerRatings = async () => {
      const sellerId = sanitizeId(resolveSellerId(product));
      if (!sellerId) {
        if (!cancelled) setSellerRatingStats({ avg: null, count: 0 });
        return;
      }
      try {
        // One canonical ratings read, shared with Profile and the reviews page.
        const stats = await ratingsRepository.summarizeForTarget(sellerId);
        if (cancelled) return;
        setSellerRatingStats(stats);
      } catch (err) {
        errorDev("ProductDetails: failed to load seller ratings", err);
        if (!cancelled) setSellerRatingStats({ avg: null, count: 0 });
      }
    };
    loadSellerRatings();
    return () => {
      cancelled = true;
    };
  }, [product?.id, product?.userId, product?.poster?.userId, product?.sellerId, product?.poster?.uid]);

  const openChatWithSeller = async () => {
    try {
      if (!currentUser) {
        toast.warning(t("farmMarketplace:loginRequired", "Please log in to continue."));
        return;
      }
      const sellerRaw = resolveSellerId(product);
      const sellerId = sanitizeId(sellerRaw);
      if (!sellerId) throw new Error("Seller info not available.");
      if (sellerId === currentUser.uid) {
        toast.info(t("product:details.ownerNotice", "This is your product."));
        return;
      }
      const chat = await getOrCreateChat({
        buyerId: currentUser.uid,
        sellerId,
        productId: product.id,
      });
      // getOrCreateChat returns either { id } or { chatId }
      const cid = chat?.id || chat?.chatId;
      if (cid) navigate(`/messages/${encodeURIComponent(sanitizeId(cid))}`);
      else toast.error(t("farmMarketplace:chatFailed", "Could not open chat. Please try again."));
    } catch (err) {
      toast.error(err?.message || t("farmMarketplace:chatFailed", "Could not open chat. Please try again."));
    }
  };

  const handleBuyNow = () => {
    const allowed = requireAuthForTransactionalAction({
      actionType: "buy_now",
      payload: {
        productId: product?.id || id,
      },
      message: t("farmMarketplace:loginPrompt", "Please log in or sign up to continue."),
    });
    if (!allowed) {
      return;
    }

    setShowBuyNowModal(true);
  };

  const goToSellerProfile = () => {
    const sellerId = sanitizeId(resolveSellerId(product));
    if (!sellerId) {
      toast.error(t("farmMarketplace:sellerUnavailable", "Seller information unavailable."));
      return;
    }
    navigate(`/profile/${sellerId}`);
  };

  const handleDelete = async () => {
    try {
      await productRepository.delete(product.id);
      toast.success(t("product:details.deleteSuccess", "Product has been deleted successfully."));
      navigate(`/profile/${currentUser?.uid || "/"}`);
    } catch (err) {
      toast.error(t("product:details.deleteFailed", "Failed to delete product. Please try again."));
    }
  };

  const handleToggleHidden = async () => {
    try {
      const nowHidden = !(product?.hidden || product?.isHidden || product?.isPrivate);
      await productRepository.updateVisibility(product.id, nowHidden);
      setMenuOpen(false);
      toast.success(
        nowHidden
          ? t("product:details.unlistedSuccess", "Product has been unlisted")
          : t("product:details.publicSuccess", "Product is now public")
      );
    } catch (err) {
      toast.error(t("product:details.visibilityFailed", "Failed to update visibility."));
    }
  };

  // Open offer modal for Orrith - Cycle of Certainty marketplace flow
  const openOfferModal = () => {
    const allowed = requireAuthForTransactionalAction({
      actionType: "make_offer",
      payload: {
        productId: product?.id || id,
      },
      message: t("farmMarketplace:loginPrompt", "Please log in or sign up to continue."),
    });
    if (!allowed) {
      return;
    }

    setResumeOfferAmount("");
    setShowOfferModal(true);
  };

  // SEO metadata for public product pages
  useEffect(() => {
    if (!product) return;

    const isPublic = !(product.hidden || product.isHidden || product.isPrivate);
    if (!isPublic) return;

    const productTitle = `${product.title || product.name} | Orrith Marketplace`;
    const productDesc = (product.desc || product.description || `${product.category || 'Agricultural product'} for sale on Orrith marketplace.`).slice(0, 160);
    const productImage = product.images?.[0] || product.image || product.thumbnail || `${SITE_ORIGIN}/logo.png`;
    const productCanonical = `${SITE_ORIGIN}/product/${product.id}`;

    applyRouteSeo({
      title: productTitle,
      description: productDesc,
      canonical: productCanonical,
      ogImage: productImage,
      ogType: "product",
    });
  }, [product]);

  useEffect(() => {
    if (!currentUser || !product?.id) return;

    const resumed = consumePendingTransactionalAction((pending) => {
      const pendingProductId = sanitizeId(pending?.payload?.productId);
      return pendingProductId && String(pendingProductId) === String(product.id);
    });

    if (!resumed) return;

    if (resumed.actionType === "make_offer") {
      const pendingAmount = resumed?.payload?.offerAmount;
      setResumeOfferAmount(pendingAmount != null ? String(pendingAmount) : "");
      setShowOfferModal(true);
      return;
    }

    if (resumed.actionType === "buy_now") {
      setShowBuyNowModal(true);
    }
  }, [currentUser, product?.id]);

  const openReportDialog = () => {
    if (!currentUser) {
      toast.warning(t("farmMarketplace:loginRequired", "Please log in to continue."));
      return;
    }
    setShowReportDialog(true);
  };

  const submitReport = async ({ reason, details }) => {
    if (!product?.id) return;
    setSubmittingReport(true);
    try {
      await submitEntityReport(currentUser, {
        entityType: REPORT_ENTITY_TYPES.PRODUCT,
        entityId: product.id,
        reason,
        details,
        entityOwnerId: sanitizeId(resolveSellerId(product)),
        metadata: {
          source: "product_details",
          productTitle: product?.title || product?.name || "",
        },
      });
      setShowReportDialog(false);
      toast.success(t("common:submitted", "Submitted successfully"));
    } catch (err) {
      toast.error(err?.message || t("common:error", "Something went wrong"));
    } finally {
      setSubmittingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-500 dark:text-gray-400">
        <div className="flex flex-col items-center gap-4">
          <div className="orrith-loader orrith-loader-lg orrith-loader-primary" />
          <p>{t("product:details.loading", "Loading product details...")}</p>
        </div>
      </div>
    );
  }

  if (!product) {
    if (privateBlocked) {
      return (
        <div className="min-h-screen bg-white dark:bg-neutral-900 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <EyeOff className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t("product:details.privateListingTitle", "Private Listing")}</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {t("product:details.privateListingDesc", "This product is private or has been unlisted by the seller.")}
            </p>
            <button
              onClick={() => navigate(-1)}
              className="ui-btn-primary px-6 py-2"
            >
              {t("common:goBack", "Go Back")}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-white dark:bg-neutral-900 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t("product:details.notFoundTitle", "Product Not Found")}</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {t("product:details.notFoundDesc", "The product you're looking for doesn't exist or has been removed.")}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="ui-btn-primary px-6 py-2"
          >
            {t("common:goBack", "Go Back")}
          </button>
        </div>
      </div>
    );
  }

  const sellerName = resolveUserDisplayName(product.poster || { name: product.sellerName }, product?.poster?.email) || "Seller";
  const sellerDisplayName =
    resolveUserDisplayName(
      sellerProfile || product.poster || { name: product.sellerName },
      sellerProfile?.email || product?.poster?.email
    ) || "Seller";
  const sellerAvgRating = firstPositiveNumber(
    sellerRatingStats?.avg,
    sellerProfile?.averageRating,
    sellerProfile?.ratingAverage,
    sellerProfile?.ratingAvg,
    sellerProfile?.rating
  );
  const sellerIp = firstPositiveNumber(
    sellerProfile?.points,
    sellerProfile?.ratingPoints,
    sellerProfile?.ip,
    sellerProfile?.ipPoints
  );
  const sellerRatingLabel = sellerAvgRating ? sellerAvgRating.toFixed(1) : t("common:new", "New");
  const sellerIpLabel = sellerIp ? `${Math.round(sellerIp).toLocaleString()} IP` : t("common:new", "New");
  const img = primaryImage(product);
  const price = formatPrice(product.price, product.currency || "RWF");
  const pricePerKg = getPricePerKg(product);
  const quantityKg = getQuantityKg(product);
  const quantityLabel = formatQuantityLabel(product, t);
  const totalPrice =
    pricePerKg != null && quantityKg != null
      ? Math.round(pricePerKg * quantityKg)
      : formatAnimalTotal(product, t);
  const isProductHidden = Boolean(product?.hidden || product?.isHidden || product?.isPrivate);
  const locShort = formatLocationShort(product.location);

  // Helper function to format date with granular time info
  const formatDateShort = (timestamp) => {
    if (!timestamp) return "Unknown";
    try {
      const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      const diffWeeks = Math.floor(diffDays / 7);
      const diffMonths = Math.floor(diffDays / 30);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
      if (diffDays === 1) return "Yesterday";
      if (diffWeeks < 1) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
      if (diffMonths < 1) return `${diffWeeks} week${diffWeeks > 1 ? "s" : ""} ago`;
      return `${diffMonths} month${diffMonths > 1 ? "s" : ""} ago`;
    } catch (err) {
      errorDev("Operation failed:", err);
      return "Unknown";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-900">
      {/* Header Navigation */}
      <div className="sticky top-0 z-40 bg-white dark:bg-neutral-800 border-b border-gray-200 dark:border-neutral-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-700 transition text-gray-700 dark:text-gray-300"
            aria-label="Go back"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div className="flex-1" />
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 pb-20 md:pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Image Section - Left Side (Mobile Full Width) */}
          <div className="lg:col-span-2">
            <div className="relative rounded-xl overflow-hidden bg-gray-200 dark:bg-neutral-800 shadow-lg">
              <div className="w-full aspect-square lg:aspect-[4/5] bg-gray-100 dark:bg-neutral-800">
                <img
                  src={images[currentImageIndex]}
                  alt={product.title || product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => (e.currentTarget.src = "/placeholder-product.png")}
                  loading="lazy"
                  onClick={() => setShowImageModal(true)}
                />
              </div>

              {/* Image Navigation */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={() =>
                      setCurrentImageIndex(
                        (p) => (p - 1 + images.length) % images.length
                      )
                    }
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2.5 transition"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() =>
                      setCurrentImageIndex(
                        (p) => (p + 1) % images.length
                      )
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2.5 transition"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>

                  {/* Image Thumbnails */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/40 to-transparent p-4 flex gap-2 overflow-x-auto">
                    {images.map((src, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentImageIndex(i)}
                        className={`w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border-2 transition ${
                          i === currentImageIndex
                            ? "border-green-500"
                            : "border-transparent opacity-60 hover:opacity-100"
                        }`}
                      >
                        <img
                          src={src}
                          alt={`Thumb ${i + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) =>
                            (e.currentTarget.src = "/placeholder-product.png")
                          }
                        />
                      </button>
                    ))}
                  </div>

                  {/* Image Counter */}
                  <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    {currentImageIndex + 1}/{images.length}
                  </div>
                </>
              )}
            </div>

            {/* Product Status Badges */}
            <div className="flex flex-wrap gap-2 mt-4">
              {isProductHidden && isOwner && (
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200 text-sm font-medium border border-yellow-200 dark:border-yellow-800">
                  <EyeOff className="w-4 h-4" />
                  {t("farmMarketplace:unlisted", "Unlisted")}
                </span>
              )}
              {product.sold && (
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white dark:bg-gray-700 text-sm font-medium">
                  <ShieldCheck className="w-4 h-4" />
                  {t("farmMarketplace:sold", "Sold")}
                </span>
              )}
            </div>
          </div>

          {/* Product Details - Right Side */}
          <div className="flex flex-col gap-6">
            {/* Compact Product Header - Horizontal Layout */}
            <div className="ui-card rounded-xl p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white break-words">
                      {product.title || product.name}
                    </h2>
                    {!isOwner && (
                      <button
                        onClick={openReportDialog}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-red-300 dark:border-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition text-xs font-medium"
                        aria-label={t("common:report", "Report")}
                        title={t("common:report", "Report")}
                      >
                        <Flag className="w-3.5 h-3.5" />
                        {t("common:report", "Report")}
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 items-center">
                    {product.category && (
                      <span className="inline-flex items-center px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded-full text-sm font-semibold">
                        {product.category}
                      </span>
                    )}
                    <span className="text-4xl font-bold text-green-600">
                      {pricePerKg != null
                        ? `${formatRwf(pricePerKg)} / KG`
                        : formatPrice(product.price, product.currency || "RWF")}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Metadata - Details Section */}
            <div className="ui-card rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t("product:details.detailsLabel", "Details")}</h3>
                {isOwner && (
                  <div className="relative" ref={menuRef}>
                    <button
                      ref={btnRef}
                      onClick={() => setMenuOpen((s) => !s)}
                      className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-700 transition"
                      aria-haspopup="true"
                      aria-label="More options"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                    </button>

                    {menuOpen && (
                      <div className="absolute right-0 mt-2 w-48 ui-card rounded-lg shadow-xl z-50 overflow-hidden">
                        <button
                          onClick={() => {
                            setMenuOpen(false);
                            navigate(`/edit-product/${product.id}`);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-neutral-700 flex items-center gap-3 transition text-gray-900 dark:text-white"
                        >
                          <Edit3 className="w-4 h-4" />
                          {t("common:edit", "Edit")}
                        </button>
                        <button
                          onClick={() => {
                            setConfirmAction({
                              type: "toggleHidden",
                              title: isProductHidden
                                ? t("product:details.makePublicConfirmTitle", "Make this product public?")
                                : t("product:details.unlistConfirmTitle", "Unlist this product?"),
                              message: isProductHidden
                                ? t("product:details.makePublicConfirmMessage", "This product will become visible in the marketplace.")
                                : t("product:details.unlistConfirmMessage", "This product will be hidden from marketplace listings."),
                              confirmLabel: isProductHidden
                                ? t("product:details.makePublic", "Make Public")
                                : t("product:details.unlist", "Unlist"),
                            });
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-neutral-700 flex items-center gap-3 transition text-gray-900 dark:text-white"
                        >
                          {isProductHidden ? (
                            <>
                              <Eye className="w-4 h-4" />
                              {t("product:details.makePublic", "Make Public")}
                            </>
                          ) : (
                            <>
                              <EyeOff className="w-4 h-4" />
                              {t("product:details.unlist", "Unlist")}
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setConfirmAction({
                              type: "delete",
                              title: t("product:details.deleteConfirmTitle", "Delete this product?"),
                              message: t("product:details.deleteConfirmMessage", "This action cannot be undone."),
                              confirmLabel: t("common:delete", "Delete"),
                              destructive: true,
                            });
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-red-50 dark:hover:bg-neutral-700 flex items-center gap-3 transition text-red-600 dark:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                          {t("common:delete", "Delete")}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-4">
                {product.category && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-neutral-700">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm">{t("product:details.category", "Category")}</span>
                    <span className="text-gray-900 dark:text-white font-semibold text-right">
                      {product.category}
                    </span>
                  </div>
                )}

                {product.productState && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-neutral-700">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm">{t("product:details.condition", "Condition")}</span>
                    <span className="text-gray-900 dark:text-white font-semibold capitalize text-right">
                      {product.productState}
                    </span>
                  </div>
                )}

                {Number(product.quantity) && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-neutral-700">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm">{t("product:details.quantity", "Quantity")}</span>
                    <span className="text-gray-900 dark:text-white font-semibold text-right">
                      {quantityLabel || `${product.quantity} ${product.quantityUnit || product.unit || "kg"}`}
                    </span>
                  </div>
                )}

                {pricePerKg != null && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-neutral-700">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm">{t("product:details.pricePerKg", "Price per kg")}</span>
                    <span className="text-gray-900 dark:text-white font-semibold text-right">
                      {formatRwf(pricePerKg)} / KG
                    </span>
                  </div>
                )}

                {totalPrice != null && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-neutral-700">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm">{t("product:details.totalPrice", "Total price")}</span>
                    <span className="text-gray-900 dark:text-white font-semibold text-right">
                      {formatRwf(totalPrice)}
                    </span>
                  </div>
                )}

                {product.location && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-neutral-700">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm">{t("product:details.location", "Location")}</span>
                    <span className="text-gray-900 dark:text-white font-semibold text-right text-sm">
                      {[
                        product.location?.cell,
                        product.location?.sector,
                        product.location?.district,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  </div>
                )}

                {product.createdAt && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-neutral-700">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm">{t("product:details.uploaded", "Uploaded")}</span>
                    <span className="text-gray-900 dark:text-white font-semibold text-right">
                      {formatDateShort(product.createdAt)}
                    </span>
                  </div>
                )}

                {product.expiry && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-neutral-700">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm">{t("product:details.expires", "Expires")}</span>
                    <span className="text-gray-900 dark:text-white font-semibold text-right">
                      {extractExpiry(product)?.toLocaleDateString()}
                    </span>
                  </div>
                )}

                {product.traySize && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-neutral-700">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm">{t("product:form.animal.units.traySize", "Tray size (eggs)")}</span>
                    <span className="text-gray-900 dark:text-white font-semibold text-right">
                      {product.traySize}
                    </span>
                  </div>
                )}

                {product.collectionDate && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-neutral-700">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm">{t("product:form.animal.dates.collectionDateLabel", "Collection date")}</span>
                    <span className="text-gray-900 dark:text-white font-semibold text-right">
                      {new Date(
                        product.collectionDate?.toDate?.() || product.collectionDate
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {product.bestBefore && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-neutral-700">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm">{t("product:form.animal.dates.bestBeforeLabel", "Best before")}</span>
                    <span className="text-gray-900 dark:text-white font-semibold text-right">
                      {new Date(
                        product.bestBefore?.toDate?.() || product.bestBefore
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {countdown && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-neutral-700">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm">{t("product:details.timeLeft", "Time Left")}</span>
                    <span className={`font-semibold text-right text-sm ${
                      expiry && expiry < new Date()
                        ? "text-red-600 dark:text-red-400"
                        : "text-green-600 dark:text-green-400"
                    }`}>
                      {countdown}
                    </span>
                  </div>
                )}

                {(product.desc || product.description) && (
                  <div className="flex justify-between items-start py-3 border-b border-gray-100 dark:border-neutral-700">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm flex-shrink-0">{t("product:details.description", "Description")}</span>
                    <span className="text-gray-900 dark:text-white font-semibold text-right text-sm whitespace-pre-line leading-relaxed flex-1 ml-4">
                      {product.desc || product.description}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Seller Info Card - Only Show if Not Owner */}
            {!isOwner && (
              <div className="ui-card rounded-xl p-6">
                <p className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-4">{t("product:details.seller", "Seller")}</p>
                <div className="flex items-center gap-4">
                  <img
                    src={
                      product.poster?.photo ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        product.poster?.name || "User"
                      )}&background=16A34A&color=fff`
                    }
                    alt={product.poster?.name || "Seller"}
                    className="w-14 h-14 rounded-full object-cover ring-2 ring-green-500/20"
                    onError={(e) =>
                      (e.currentTarget.src = "/default-avatar.png")
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={goToSellerProfile}
                      className="text-lg font-bold text-gray-900 dark:text-white hover:text-green-600 dark:hover:text-green-400 transition block truncate"
                    >
                      {sellerDisplayName}
                    </button>
                    <div className="mt-1 flex flex-wrap gap-2 text-xs">
                      <span className="px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 font-semibold">
                        {sellerRatingLabel} | {sellerIpLabel}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons - Marketplace Interaction */}
            {!isOwner && (
              <div className="sticky bottom-0 mobile-nav-safe-inset lg:relative bg-card border-t border-border p-4 -mx-6 mb-0 lg:mb-0 lg:p-0 lg:border-0 lg:bg-transparent">
                <div className="flex gap-2 lg:gap-3">
                  <button
                    onClick={handleBuyNow}
                    disabled={isExpired || product.sold}
                    className={`flex-1 px-4 lg:px-6 py-3 lg:py-3.5 rounded-lg font-bold text-base lg:text-lg transition flex items-center justify-center gap-2 ${
                      isExpired || product.sold
                        ? "bg-gray-300 text-gray-600 cursor-not-allowed dark:bg-neutral-700 dark:text-gray-500"
                        : "bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl"
                    }`}
                  >
                    {t("farmMarketplace:buyNow", "Buy Now")}
                  </button>

                  <button
                    onClick={openOfferModal}
                    disabled={isExpired || product.sold}
                    className={`flex-1 px-4 lg:px-6 py-3 lg:py-3.5 rounded-lg font-bold text-base lg:text-lg transition flex items-center justify-center gap-2 ${
                      isExpired || product.sold
                        ? "bg-gray-200 text-gray-600 cursor-not-allowed dark:bg-neutral-700 dark:text-gray-500"
                        : "border-2 border-teal-600 text-teal-600 dark:border-teal-400 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-neutral-700/50"
                    }`}
                  >
                    {t("farmMarketplace:makeOffer", "Make Offer")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Similar products — relevance-first recommendations. Rendered below
            the image/detail grid so it flows beneath the product detail images
            on desktop and below the details on mobile. */}
        {product && (
          <SimilarProducts product={product} currentUser={currentUser} />
        )}
      </main>

      {/* Modals */}
      {showImageModal && (
        <ImageModal
          images={images}
          start={currentImageIndex}
          onClose={() => setShowImageModal(false)}
        />
      )}

      {showOfferModal && product && (
        <OfferModal
          product={product}
          initialAmount={resumeOfferAmount}
          onClose={() => {
            setShowOfferModal(false);
            setResumeOfferAmount("");
          }}
          onSend={() => {
            setShowOfferModal(false);
            setResumeOfferAmount("");
          }}
        />
      )}

      <BuyNowGuidanceModal
        open={showBuyNowModal}
        onClose={() => setShowBuyNowModal(false)}
        onMakeOffer={() => setShowOfferModal(true)}
      />

      <ConfirmDialog
        open={Boolean(confirmAction)}
        title={confirmAction?.title || t("common:confirm", "Confirm")}
        message={confirmAction?.message || ""}
        confirmLabel={confirmAction?.confirmLabel || t("common:confirm", "Confirm")}
        cancelLabel={t("common:cancel", "Cancel")}
        destructive={Boolean(confirmAction?.destructive)}
        onCancel={() => setConfirmAction(null)}
        onConfirm={async () => {
          const type = confirmAction?.type;
          setConfirmAction(null);
          if (type === "delete") {
            await handleDelete();
            return;
          }
          if (type === "toggleHidden") {
            await handleToggleHidden();
          }
        }}
      />

      <ReportEntityDialog
        open={showReportDialog}
        entityLabel="product"
        submitting={submittingReport}
        onSubmit={submitReport}
        onClose={() => {
          if (submittingReport) return;
          setShowReportDialog(false);
        }}
      />
    </div>
  );
}


